import { useRef } from "react";

/**
 * Six digits, in six boxes.
 *
 * Brief 01 asks for a code step that "makes six digits obvious", and the
 * board draws six separate boxes. It was one field with `maxLength={6}`,
 * which is the same keystrokes and none of the reassurance: a person holding
 * a phone in one hand wants to see how many are left without counting the
 * characters they have typed.
 *
 * Six boxes are usually where paste goes to die, so paste is handled first
 * and the rest is built around it. An authenticator code is read off another
 * screen and very often copied, and a field that silently drops five of the
 * six digits is worse than the single field it replaced.
 *
 * The value is a plain string and the boxes are a view of it. Nothing here
 * holds a digit of its own, so there is no state to disagree with itself —
 * the bug these inputs are famous for.
 */
export function CodeInput({
  value,
  onChange,
  length = 6,
  autoFocus = false,
  invalid = false,
  label = "Authentication code",
}: {
  value: string;
  onChange: (next: string) => void;
  length?: number;
  autoFocus?: boolean;
  /** A refused code. The boxes carry it, not only the sentence under them. */
  invalid?: boolean;
  label?: string;
}) {
  const boxes = useRef<(HTMLInputElement | null)[]>([]);

  const digits = value.padEnd(length, " ").slice(0, length).split("");

  function focus(index: number) {
    const at = Math.max(0, Math.min(length - 1, index));
    boxes.current[at]?.focus();
    boxes.current[at]?.select();
  }

  /** Write one digit at a position and return the whole code. */
  function written(index: number, digit: string) {
    const next = value.padEnd(length, " ").slice(0, length).split("");
    next[index] = digit || " ";
    return next.join("").trimEnd();
  }

  function onType(index: number, raw: string) {
    // The last character typed, so overwriting a filled box works the way a
    // person expects rather than being refused because the box is full.
    const digit = raw.replace(/\D/g, "").slice(-1);
    if (!digit) return;
    onChange(written(index, digit));
    if (index < length - 1) focus(index + 1);
  }

  function onKey(index: number, event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Backspace") {
      event.preventDefault();
      if ((digits[index] ?? "").trim()) {
        onChange(written(index, ""));
        return;
      }
      // Already empty: clear the one before and go there, which is what
      // holding backspace is for.
      if (index > 0) {
        onChange(written(index - 1, ""));
        focus(index - 1);
      }
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      focus(index - 1);
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      focus(index + 1);
    }
  }

  function onPaste(index: number, event: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    event.preventDefault();

    const next = value.padEnd(length, " ").slice(0, length).split("");
    for (let at = 0; at < pasted.length && index + at < length; at++) {
      next[index + at] = pasted[at] ?? " ";
    }
    onChange(next.join("").trimEnd());
    focus(index + pasted.length);
  }

  return (
    <div
      className="code-input"
      role="group"
      aria-label={label}
      data-invalid={invalid || undefined}
    >
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            boxes.current[index] = el;
          }}
          className="code-box"
          type="text"
          inputMode="numeric"
          /* One field carries the hint, so a password manager or an SMS
             autofill has somewhere to put the code; the paste handler then
             spreads it across the rest. */
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={digit.trim()}
          aria-label={`Digit ${index + 1} of ${length}`}
          autoFocus={autoFocus && index === 0}
          onChange={(event) => onType(index, event.target.value)}
          onKeyDown={(event) => onKey(index, event)}
          onPaste={(event) => onPaste(index, event)}
          onFocus={(event) => event.target.select()}
        />
      ))}
    </div>
  );
}
